import { useState, useCallback, useMemo, useEffect, useRef, type MouseEvent as ReactMouseEvent } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, IconButton,
  Tabs, Tab, MenuItem, Chip, Checkbox, FormControlLabel,
  List, ListItemButton, ListItemText, Divider,
  Tooltip, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
  Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon, Edit as EditIcon,
  Delete as DeleteIcon, CheckCircle as ActiveIcon,
  Map as MapIcon, Add as AddIcon,
} from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AdminListPanel } from './AdminListPanel';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';
import { ConfirmDialog } from '../../shared/components/Dialogs';
import {
  SecurityEditor, ReadingWorkflowEditor, CDSWorkflowEditor,
  ReportConfigEditor, SiteDefaultsEditor, InsurancePaymentEditor,
  ImageViewingEditor, FeatureConfigEditor,
  LoginConfigEditor, HL7ConfigEditor,
  BillingEditor, PanelConfigEditor,
} from './SiteCategoryEditors';
import type { SiteRecord, InsuranceCompanyBean, MacroBean } from '../../api/types';

/* ── Grid column definitions ── */
const columns: GridColDef[] = [
  { field: 'siteId', headerName: 'Site ID', width: 100 },
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'address', headerName: 'Address', flex: 1.5, minWidth: 200 },
  { field: 'contact', headerName: 'Contact', width: 120,
    valueGetter: (_v: unknown, row: SiteRecord) => {
      const c = row.contact;
      if (!c) return '';
      if (typeof c === 'string') return c;
      return '';
    },
  },
  { field: 'phone', headerName: 'Phone', width: 120 },
  { field: 'fax', headerName: 'Fax', width: 120 },
  { field: 'primaryAE', headerName: 'Primary AE', width: 110,
    valueGetter: (_v: unknown, row: SiteRecord) => det(row).AE_TITLE ?? '',
  },
  { field: 'secondaryAE', headerName: 'Secondary AE', width: 110,
    valueGetter: (_v: unknown, row: SiteRecord) => det(row).SEC_AE_TITLE ?? '',
  },
  { field: 'timeZone', headerName: 'Timezone', width: 140 },
  {
    field: 'status',
    headerName: 'Status',
    width: 80,
    renderCell: (params) => {
      const active = !params.row.status || params.row.status === 'active';
      return <Chip label={active ? 'Active' : 'Inactive'} size="small" color={active ? 'success' : 'default'} sx={{ fontSize: 11, height: 20 }} />;
    },
  },
  {
    field: 'actions',
    headerName: '',
    width: 80,
    sortable: false,
    renderCell: () => null,
  },
];

/** Shorthand to get details blob from a SiteRecord */
const det = (s: SiteRecord) => (s.details ?? {}) as Record<string, unknown>;

/* ── Fix Leaflet default marker icons (webpack/vite strips them) ── */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

/* ── Highlighted marker icon (red) ── */
const highlightIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

/* ── Geocode cache — avoid re-geocoding the same address ── */
const geocodeCache = new Map<string, [number, number] | null>();

/**
 * Geocode an address using OSM Nominatim (free, no API key).
 * Returns [lat, lng] or null. Caches results.
 * Strips suite/apt/unit numbers that confuse Nominatim.
 */
async function geocodeAddress(address: string): Promise<[number, number] | null> {
  if (!address) return null;
  const cached = geocodeCache.get(address);
  if (cached !== undefined) return cached;

  // Try the original address first, then try cleaned versions
  const variants = [address];
  // Strip suite/apt/unit/# patterns (e.g. "Ste 400", "Suite 200", "Apt 3B", "#5")
  const cleaned = address
    .replace(/,?\s*(ste|suite|apt|apartment|unit|#)\s*[\w-]+/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned !== address) variants.push(cleaned);

  for (const variant of variants) {
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(variant)}`,
        { headers: { 'User-Agent': 'BritPACS/1.0' } },
      );
      const data = await resp.json();
      if (Array.isArray(data) && data.length > 0) {
        const result: [number, number] = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        geocodeCache.set(address, result);
        return result;
      }
    } catch { /* ignore geocoding failures */ }
  }
  geocodeCache.set(address, null);
  return null;
}

/* ── Site Map Component ── */
const SiteMap = ({
  sites,
  selectedSite,
  onSiteClick,
}: {
  sites: SiteRecord[];
  selectedSite?: SiteRecord | null;
  onSiteClick?: (site: SiteRecord) => void;
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const markerMapRef = useRef<Map<string, L.Marker>>(new Map());
  // Track resolved coords per site (from latLong or geocoding)
  const resolvedCoordsRef = useRef<Map<string, [number, number]>>(new Map());

  // Initialize the map once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      center: [39.509, -98.434], // US center (same as GWT default)
      zoom: 4,
      scrollWheelZoom: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    markersRef.current = L.layerGroup().addTo(map);
    mapInstance.current = map;

    return () => {
      map.remove();
      mapInstance.current = null;
      markersRef.current = null;
    };
  }, []);

  // Helper: add a marker for a site at given coords
  const addMarker = useCallback(
    (site: SiteRecord, lat: number, lng: number) => {
      const markers = markersRef.current;
      if (!markers) return;

      const isSelected = selectedSite?.objKey === site.objKey;
      const marker = L.marker([lat, lng], isSelected ? { icon: highlightIcon } : {})
        .bindPopup(`<b>${site.name}</b><br/>${site.address ?? ''}<br/>${site.phone ?? ''}`)
        .bindTooltip(site.name, { direction: 'top', offset: [0, -30] });

      if (onSiteClick) {
        marker.on('click', () => onSiteClick(site));
      }

      markers.addLayer(marker);
      markerMapRef.current.set(site.objKey, marker);
    },
    [selectedSite, onSiteClick],
  );

  // Update markers when sites or selection changes
  useEffect(() => {
    const markers = markersRef.current;
    const map = mapInstance.current;
    if (!markers || !map) return;

    markers.clearLayers();
    markerMapRef.current.clear();

    let cancelled = false;
    const boundsArr: L.LatLngExpression[] = [];

    // Phase 1: add markers for sites that already have coords (latLong or cache)
    const needsGeocode: SiteRecord[] = [];
    for (const site of sites) {
      const d = det(site);
      const latLong = d.latLong as number[] | undefined;
      if (latLong && latLong.length === 2 && !(latLong[0] === 0 && latLong[1] === 0)) {
        resolvedCoordsRef.current.set(site.objKey, [latLong[0], latLong[1]]);
        addMarker(site, latLong[0], latLong[1]);
        boundsArr.push([latLong[0], latLong[1]]);
      } else if (resolvedCoordsRef.current.has(site.objKey)) {
        const [lat, lng] = resolvedCoordsRef.current.get(site.objKey)!;
        addMarker(site, lat, lng);
        boundsArr.push([lat, lng]);
      } else if (site.address) {
        needsGeocode.push(site);
      }
    }

    // Phase 2: geocode sites that need it (async, staggered to respect Nominatim rate limits)
    if (needsGeocode.length > 0) {
      (async () => {
        for (let i = 0; i < needsGeocode.length; i++) {
          if (cancelled) break;
          const site = needsGeocode[i];
          // Rate limit: 1 req/s for Nominatim
          if (i > 0) await new Promise((r) => setTimeout(r, 1100));
          if (cancelled) break;
          const coords = await geocodeAddress(site.address!);
          if (cancelled) break;
          if (coords) {
            resolvedCoordsRef.current.set(site.objKey, coords);
            addMarker(site, coords[0], coords[1]);
            boundsArr.push([coords[0], coords[1]]);
            // After each geocode, refit if no site selected
            if (!selectedSite && boundsArr.length > 0) {
              map.fitBounds(L.latLngBounds(boundsArr), { padding: [40, 40], maxZoom: 12 });
            }
          }
        }
      })();
    }

    // Initial fit/zoom
    if (selectedSite) {
      const coords = resolvedCoordsRef.current.get(selectedSite.objKey);
      if (coords) {
        map.setView([coords[0], coords[1]], 13, { animate: true });
        // Open popup after a tick (marker may be added async)
        setTimeout(() => {
          const m = markerMapRef.current.get(selectedSite.objKey);
          if (m) m.openPopup();
        }, 200);
      } else if (selectedSite.address) {
        // Geocode the selected site immediately
        geocodeAddress(selectedSite.address).then((c) => {
          if (cancelled || !c) return;
          resolvedCoordsRef.current.set(selectedSite.objKey, c);
          addMarker(selectedSite, c[0], c[1]);
          map.setView([c[0], c[1]], 13, { animate: true });
          setTimeout(() => {
            const m = markerMapRef.current.get(selectedSite.objKey);
            if (m) m.openPopup();
          }, 200);
        });
      }
    } else if (boundsArr.length > 0) {
      map.fitBounds(L.latLngBounds(boundsArr), { padding: [40, 40], maxZoom: 12 });
    }

    return () => { cancelled = true; };
  }, [sites, selectedSite, onSiteClick, addMarker]);

  // Invalidate size when container resizes (including divider drag)
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    const timer = setTimeout(() => map.invalidateSize(), 100);
    const onResize = () => { setTimeout(() => map.invalidateSize(), 50); };
    window.addEventListener('resize', onResize);
    return () => { clearTimeout(timer); window.removeEventListener('resize', onResize); };
  });

  return (
    <div
      ref={mapRef}
      style={{ width: '100%', height: '100%', minHeight: 200 }}
    />
  );
};

/**
 * Sites admin tab — SiteListPanel equivalent.
 *
 * Grid: Site ID, Name, Address, Contact, Phone, Fax, Primary AE, Secondary AE, Timezone, Status.
 * Toolbar: Add Site, Delete (inactivate), Make Active, Show Inactive, Refresh.
 * Edit: GWT-style sidebar navigation with 26 categories covering all site config.
 */
export const SitesPanel = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editSite, setEditSite] = useState<SiteRecord | null>(null);
  const [isNewSite, setIsNewSite] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SiteRecord | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<SiteRecord | null>(null);
  const [siteRefs, setSiteRefs] = useState<{ users: number; modalities: number; locations: number; studies: number; patients: number; enterpriseChildren: number } | null>(null);
  const [showMap, setShowMap] = useState(true);
  const [selectedSite, setSelectedSite] = useState<SiteRecord | null>(null);
  const [splitPct, setSplitPct] = useState(60);
  const containerRef = useRef<HTMLDivElement>(null);

  /* ── Drag-to-resize handler for the split pane divider ── */
  const handleDividerMouseDown = useCallback((e: ReactMouseEvent) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const startX = e.clientX;
    const startPct = splitPct;
    const containerW = container.getBoundingClientRect().width;
    const onMove = (me: globalThis.MouseEvent) => {
      const dx = me.clientX - startX;
      const newPct = Math.min(85, Math.max(25, startPct + (dx / containerW) * 100));
      setSplitPct(newPct);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Invalidate Leaflet map size after resize
      window.dispatchEvent(new Event('resize'));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [splitPct]);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.sites.all,
    queryFn: () => api.getSites(),
    staleTime: 60_000,
  });

  const sites = useMemo(() => {
    const all = data?.records ?? [];
    let filtered = all;
    if (!showInactive) {
      filtered = filtered.filter((s) => !s.status || s.status === 'active');
    }
    if (!search) return filtered;
    const s = search.toLowerCase();
    return filtered.filter(
      (st) =>
        st.name.toLowerCase().includes(s) ||
        st.siteId.toLowerCase().includes(s) ||
        (st.address ?? '').toLowerCase().includes(s) ||
        (st.phone ?? '').toLowerCase().includes(s) ||
        ((det(st).AE_TITLE as string) ?? '').toLowerCase().includes(s),
    );
  }, [data, search, showInactive]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.sites.all });
  }, [queryClient]);

  const handleDeleteAttempt = useCallback(async (site: SiteRecord) => {
    try {
      const refs = await api.hasSiteReferences(site.objKey);
      setSiteRefs(refs.hasReferences ? refs : null);
    } catch { setSiteRefs(null); }
    setDeleteTarget(site);
  }, []);

  const handlePermanentDeleteAttempt = useCallback(async (site: SiteRecord) => {
    try {
      const refs = await api.hasSiteReferences(site.objKey);
      setSiteRefs(refs.hasReferences ? refs : null);
    } catch { setSiteRefs(null); }
    setPermanentDeleteTarget(site);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await api.addModifySite({ objKey: deleteTarget.objKey, details: { status: 'inactive' } });
      pushSuccess(`Site "${deleteTarget.siteId}" inactivated`);
      refresh();
    } catch (e) {
      pushError(`Failed to inactivate site: ${e instanceof Error ? e.message : String(e)}`);
    }
    setDeleteTarget(null);
    setSiteRefs(null);
  }, [deleteTarget, refresh]);

  const handleMakeActive = useCallback(async (site: SiteRecord) => {
    try {
      await api.addModifySite({ objKey: site.objKey, details: { status: null } });
      pushSuccess(`Site "${site.siteId}" activated`);
      refresh();
    } catch (e) {
      pushError(`Failed to activate site: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [refresh]);

  const handlePermanentDelete = useCallback(async () => {
    if (!permanentDeleteTarget) return;
    try {
      await api.deleteSite(permanentDeleteTarget.objKey);
      pushSuccess(`Site "${permanentDeleteTarget.siteId}" permanently deleted`);
      refresh();
    } catch (e) {
      pushError(`Failed to delete site: ${e instanceof Error ? e.message : String(e)}`);
    }
    setPermanentDeleteTarget(null);
    setSiteRefs(null);
  }, [permanentDeleteTarget, refresh]);

  return (
    <>
      <AdminListPanel
        title="Sites"
        search={search}
        onSearchChange={setSearch}
        onRefresh={refresh}
        onAdd={() => {
          setEditSite({ objKey: '', siteId: '', name: '', address: '', phone: '', fax: '', contact: '', timeZone: '', details: {} });
          setIsNewSite(true);
        }}
        statusText={`${sites.length} sites`}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.25, gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} sx={{ p: 0.25 }} />}
            label={<Typography sx={{ fontSize: 11 }}>Show Inactive</Typography>}
            sx={{ ml: 0 }}
          />
          <Tooltip title={showMap ? 'Hide Map' : 'Show Map'}>
            <IconButton size="small" onClick={() => setShowMap((v) => !v)} sx={{ p: 0.25 }}>
              <MapIcon sx={{ fontSize: 16, color: showMap ? 'primary.main' : 'action.disabled' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box ref={containerRef} sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* Grid side */}
          <Box sx={{ flex: showMap ? `0 0 ${splitPct}%` : '1 1 100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <DataGrid
          rows={sites}
          onRowClick={(params) => {
            const site = params.row as SiteRecord;
            setSelectedSite((prev) => prev?.objKey === site.objKey ? null : site);
          }}
          columns={columns.map((col) =>
            col.field === 'actions'
              ? {
                  ...col,
                  renderCell: (params) => {
                    const site = params.row as SiteRecord;
                    const isInactive = site.status && site.status !== 'active';
                    return (
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        <IconButton size="small" onClick={() => setEditSite(site)} sx={{ width: 24, height: 24 }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        {isInactive ? (<>
                          <Tooltip title="Make Active">
                            <IconButton size="small" onClick={() => handleMakeActive(site)} sx={{ width: 24, height: 24 }}>
                              <ActiveIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Permanently Delete">
                            <IconButton size="small" onClick={() => handlePermanentDeleteAttempt(site)} sx={{ width: 24, height: 24 }}>
                              <DeleteIcon sx={{ fontSize: 14, color: 'error.main' }} />
                            </IconButton>
                          </Tooltip>
                        </>) : (
                          <IconButton size="small" onClick={() => handleDeleteAttempt(site)} sx={{ width: 24, height: 24 }}>
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    );
                  },
                }
              : col,
          )}
          getRowId={(row) => (row as SiteRecord).objKey}
          loading={isLoading}
          density="compact"
          disableColumnMenu
          rowHeight={24}
          columnHeaderHeight={28}
          sx={{
            flex: 1,
            border: 'none',
            fontSize: 12,
            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: 'action.selected' },
            /* Pin the actions column to the right edge so edit/delete buttons are always visible */
            '& .MuiDataGrid-cell[data-field="actions"]': {
              position: 'sticky', right: 0, zIndex: 1,
              backgroundColor: 'inherit',
            },
            '& .MuiDataGrid-columnHeader[data-field="actions"]': {
              position: 'sticky', right: 0, zIndex: 2,
              backgroundColor: 'inherit',
            },
          }}
        />
          </Box>
          {/* Draggable divider */}
          {showMap && (
            <Box
              onMouseDown={handleDividerMouseDown}
              sx={{
                width: 6, flexShrink: 0, cursor: 'col-resize',
                backgroundColor: 'divider', '&:hover': { backgroundColor: 'primary.main' },
                transition: 'background-color 0.15s',
              }}
            />
          )}
          {/* Map side */}
          {showMap && (
            <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
              <SiteMap sites={sites} selectedSite={selectedSite} onSiteClick={(site) => { setSelectedSite(site); setEditSite(site); }} />
            </Box>
          )}
        </Box>
      </AdminListPanel>

      {editSite && <SiteDialog site={editSite} isNew={isNewSite} open onClose={() => { setEditSite(null); setIsNewSite(false); }} onSave={() => { setEditSite(null); setIsNewSite(false); refresh(); }} />}

      {deleteTarget && (
        <ConfirmDialog open title="Inactivate Site"
          message={siteRefs
            ? `Site "${deleteTarget.name}" (${deleteTarget.siteId}) has ${siteRefs.users} users, ${siteRefs.modalities} modalities, ${siteRefs.locations} locations, ${siteRefs.studies} studies, ${siteRefs.patients} patients, and ${siteRefs.enterpriseChildren} child sites. Are you sure you want to inactivate it?`
            : `Are you sure you want to inactivate site "${deleteTarget.name}" (${deleteTarget.siteId})? The site can be reactivated later.`}
          destructive onConfirm={handleDelete} onCancel={() => { setDeleteTarget(null); setSiteRefs(null); }} />
      )}

      {permanentDeleteTarget && (
        <ConfirmDialog open title="Permanently Delete Site"
          message={siteRefs
            ? `WARNING: Site "${permanentDeleteTarget.name}" (${permanentDeleteTarget.siteId}) has ${siteRefs.users} users, ${siteRefs.modalities} modalities, ${siteRefs.locations} locations, ${siteRefs.studies} studies, ${siteRefs.patients} patients, and ${siteRefs.enterpriseChildren} child sites. Permanently deleting will remove ALL associated data. This CANNOT be undone.`
            : `WARNING: This will permanently delete site "${permanentDeleteTarget.name}" (${permanentDeleteTarget.siteId}) and ALL associated data. This action CANNOT be undone. Are you absolutely sure?`}
          destructive onConfirm={handlePermanentDelete} onCancel={() => { setPermanentDeleteTarget(null); setSiteRefs(null); }} />
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Sidebar categories — mirrors GWT SiteDialog.java InitComponents()
 * ══════════════════════════════════════════════════════════════════════════ */
type Cat =
  | 'siteInfo' | 'loginConfig' | 'hl7' | 'dicom' | 'accessGroups'
  | 'urgentWorks' | 'discordance' | 'peerReview' | 'patientDiscovery'
  | 'procedures' | 'insurancePayment' | 'imageViewing' | 'reportConfig'
  | 'dictation' | 'panelConfig' | 'siteDefaults' | 'security' | 'qc'
  | 'readingWorkflow' | 'notifications' | 'viewerInterfaces' | 'cdsWorkflow'
  | 'userRoles' | 'logs' | 'billingInvoice' | 'licensedFeatures' | 'macros' | 'insuranceCompanies';

const SIDEBAR: { id: Cat; label: string }[] = [
  { id: 'siteInfo', label: 'Site Information' },
  { id: 'loginConfig', label: 'Login Configuration' },
  { id: 'hl7', label: 'HL7' },
  { id: 'dicom', label: 'DICOM' },
  { id: 'accessGroups', label: 'Access Groups' },
  { id: 'urgentWorks', label: 'Urgent Works' },
  { id: 'discordance', label: 'Discordance' },
  { id: 'peerReview', label: 'Peer Review' },
  { id: 'patientDiscovery', label: 'Patient Discovery' },
  { id: 'procedures', label: 'Procedures' },
  { id: 'insurancePayment', label: 'Insurance and Payment' },
  { id: 'insuranceCompanies', label: 'Insurance Companies' },
  { id: 'imageViewing', label: 'Image Viewing' },
  { id: 'reportConfig', label: 'Report Configuration' },
  { id: 'dictation', label: 'Dictation System' },
  { id: 'panelConfig', label: 'Panel Configuration' },
  { id: 'siteDefaults', label: 'Site Defaults' },
  { id: 'macros', label: 'Site Macros' },
  { id: 'security', label: 'Security' },
  { id: 'qc', label: 'QC' },
  { id: 'readingWorkflow', label: 'Reading Workflow and Tools' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'viewerInterfaces', label: 'Viewer Interfaces' },
  { id: 'cdsWorkflow', label: 'CDS Workflow' },
  { id: 'userRoles', label: 'User Roles' },
  { id: 'logs', label: 'Logs' },
  { id: 'billingInvoice', label: 'Billing Invoice' },
  { id: 'licensedFeatures', label: 'Licensed Features' },
];

/* ══════════════════════════════════════════════════════════════════════════
 * SiteDialog — full-size editor with sidebar navigation (26 categories)
 * ══════════════════════════════════════════════════════════════════════════ */
const TIMEZONES = [
  'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific', 'US/Alaska', 'US/Hawaii',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'AMERICA_NEWYORK', 'AMERICA_CHICAGO', 'AMERICA_DENVER', 'AMERICA_LOSANGELES',
  'UTC', 'Europe/London', 'Europe/Berlin', 'Asia/Tokyo', 'Australia/Sydney',
];
const DATE_FORMATS = [{ value: 0, label: 'MM/DD/YYYY' }, { value: 1, label: 'DD/MM/YYYY' }, { value: 2, label: 'YYYY/MM/DD' }];
const CLOCK_FORMATS = [{ value: 0, label: '12-hour (AM/PM)' }, { value: 1, label: '24-hour' }];
const DATE_SEPARATORS = [{ value: 0, label: '/' }, { value: 1, label: '-' }, { value: 2, label: '.' }];
const LANGUAGES = [{ value: '', label: 'Default (English)' }, { value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }, { value: 'fr', label: 'French' }, { value: 'de', label: 'German' }, { value: 'pt', label: 'Portuguese' }, { value: 'ja', label: 'Japanese' }];
const DAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'] as const;

const SiteDialog = ({ site, isNew, open, onClose, onSave }: { site: SiteRecord; isNew?: boolean; open: boolean; onClose: () => void; onSave: () => void }) => {
  const [cat, setCat] = useState<Cat>('siteInfo');
  const [siteInfoTab, setSiteInfoTab] = useState(0);

  const d = useMemo(() => JSON.parse(JSON.stringify(site.details ?? {})) as Record<string, unknown>, [site]);

  // Site ID — editable only when creating a new site
  const [siteId, setSiteId] = useState(site.siteId);

  // Core fields
  const [name, setName] = useState(site.name);
  const [address, setAddress] = useState(site.address ?? '');
  const [contactField, setContactField] = useState(typeof site.contact === 'string' ? site.contact : '');
  const [phone, setPhone] = useState(site.phone ?? '');

  // Details fields
  const [fax, setFax] = useState((site.fax ?? d.fax ?? '') as string);
  const [timeZone, setTimeZone] = useState((site.timeZone ?? d.siteTimeZone ?? '') as string);
  const [dateFormat, setDateFormat] = useState((site.dateFormat ?? d.siteDateFormat ?? 0) as number);
  const [clockFormat, setClockFormat] = useState((site.clockFormat ?? d.siteClockFormat ?? 0) as number);
  const [dateSeparator, setDateSeparator] = useState((site.dateSeparator ?? d.siteSeparator ?? 0) as number);
  const [language, setLanguage] = useState((site.language ?? d.language ?? '') as string);
  const [enterpriseSite, setEnterpriseSite] = useState((d.enterpriseSite ?? '') as string);

  // DICOM
  const [aeTitle, setAeTitle] = useState((d.AE_TITLE ?? '') as string);
  const [secAeTitle, setSecAeTitle] = useState((d.SEC_AE_TITLE ?? '') as string);
  const [compAeTitle, setCompAeTitle] = useState((d.COMP_AE_TITLE ?? '') as string);
  const [allowNonStrictDicom, setAllowNonStrictDicom] = useState(!!d.allowNonStrictDicom);
  const ac = (d.autoComplete ?? { time: 1, scale: 'm' }) as { time: number; scale: string };
  const [acTime, setAcTime] = useState(ac.time);
  const [acScale, setAcScale] = useState(ac.scale);
  const sac = (d.secAutoComplete ?? { time: 1, scale: 'm' }) as { time: number; scale: string };
  const [sacTime, setSacTime] = useState(sac.time);
  const [sacScale, setSacScale] = useState(sac.scale);

  // Storage
  const stor = (d.storage ?? {}) as Record<string, unknown>;
  const [retainViewed, setRetainViewed] = useState((stor.retainViewed ?? 7) as number);
  const [retainUnit, setRetainUnit] = useState((stor.retainViewedUnit ?? 'DAYS') as string);
  const [purgeType, setPurgeType] = useState((stor.purgeType ?? 'NEVER') as string);
  const [replication, setReplication] = useState((stor.replicationCount ?? 1) as number);

  // Number generation
  const gn = (d.generate_numbers ?? {}) as Record<string, unknown>;
  const [accPfx, setAccPfx] = useState((gn.accName ?? 'OID') as string);
  const [accLen, setAccLen] = useState((gn.accLen ?? 9) as number);
  const [accSt, setAccSt] = useState((gn.accStart ?? 1) as number);
  const [pidPfx, setPidPfx] = useState((gn.pidName ?? 'PID') as string);
  const [pidLen, setPidLen] = useState((gn.pidLen ?? 9) as number);
  const [pidSt, setPidSt] = useState((gn.pidStart ?? 1) as number);
  const [bidPfx, setBidPfx] = useState((gn.bidName ?? 'INS') as string);
  const [bidLen, setBidLen] = useState((gn.bidLen ?? 6) as number);
  const [bidSt, setBidSt] = useState((gn.bidStart ?? 1) as number);

  // Toggles
  const [openReg, setOpenReg] = useState(!!d.openRegistration);
  const [anonUpload, setAnonUpload] = useState(!!d.allowAnonymousUpload);
  const [noRetention, setNoRetention] = useState(!!d.disallowRetention);
  const [redirect, setRedirect] = useState(!!d.redirectToSiteURL);
  const [addToEnterprise, setAddToEnterprise] = useState(!!d.addPatientToEnterpriseSite);

  // Hours
  const hoursBlob = (d.hours ?? { week: {}, vacation: [] }) as { week: Record<string, { openTime?: string; closeTime?: string; doors: string }>; vacation: unknown[] };
  const [hours, setHours] = useState(hoursBlob.week);

  // Licensed features (read-only display)
  const lf = (d.licensedFeatures ?? {}) as Record<string, unknown>;

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const objKey = isNew ? `site:${siteId}` : site.objKey;
      await api.addModifySite({
        objKey, name, address, contact: contactField, phone,
        details: {
          fax, siteTimeZone: timeZone, siteDateFormat: dateFormat, siteClockFormat: clockFormat,
          siteSeparator: dateSeparator, language, enterpriseSite,
          AE_TITLE: aeTitle, SEC_AE_TITLE: secAeTitle, COMP_AE_TITLE: compAeTitle,
          allowNonStrictDicom,
          autoComplete: { time: acTime, scale: acScale },
          secAutoComplete: { time: sacTime, scale: sacScale },
          storage: { retainViewed, retainViewedUnit: retainUnit, purgeType, replicationCount: replication },
          generate_numbers: { accName: accPfx, accLen, accStart: accSt, pidName: pidPfx, pidLen, pidStart: pidSt, bidName: bidPfx, bidLen, bidStart: bidSt },
          openRegistration: openReg, allowAnonymousUpload: anonUpload, disallowRetention: noRetention,
          redirectToSiteURL: redirect, addPatientToEnterpriseSite: addToEnterprise,
          hours: { week: hours, vacation: hoursBlob.vacation },
        },
      });
      pushSuccess(isNew ? `Site "${siteId}" created successfully` : 'Site saved successfully');
      onSave();
    } catch (e) { pushError(`Failed to save site: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setSaving(false); }
  };

  const setH = (day: string, field: 'openTime' | 'closeTime' | 'doors', v: string) =>
    setHours((p) => ({ ...p, [day]: { ...(p[day] ?? { doors: 'Closed' }), [field]: v } }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: '90vw', maxWidth: 1100, height: '88vh', borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg, display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 0, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail, minHeight: 40 }}>
        {isNew ? 'Add Site' : `Edit Site — ${site.siteId}`}
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </DialogTitle>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: 210, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto' }}>
          <List dense disablePadding>
            {SIDEBAR.map((item) => (
              <ListItemButton key={item.id} selected={cat === item.id} onClick={() => setCat(item.id)}
                sx={{ py: 0.5, px: 1.5, '&.Mui-selected': { backgroundColor: 'action.selected' } }}>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 12, fontWeight: cat === item.id ? 600 : 400 }} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontSize: 14, fontWeight: 600, mb: 1.5, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
            {SIDEBAR.find((i) => i.id === cat)?.label ?? ''}
          </Typography>

          {/* ── Site Information ── */}
          {cat === 'siteInfo' && <>
            <Tabs value={siteInfoTab} onChange={(_, v) => setSiteInfoTab(v)} sx={{ mb: 1.5, minHeight: 32 }}>
              <Tab label="General" sx={tabSx} /><Tab label="Site Hours" sx={tabSx} />
            </Tabs>
            {siteInfoTab === 0 && <Box>
              <FR l="Site ID *">{isNew
                ? <TextField size="small" fullWidth value={siteId} onChange={(e) => setSiteId(e.target.value.toUpperCase().replace(/\s/g, '_'))} sx={fsx} placeholder="e.g. MY_SITE" />
                : <TextField size="small" fullWidth value={site.siteId} disabled sx={fsx} />
              }</FR>
              <FR l="Name *"><TextField size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)} sx={fsx} /></FR>
              <FR l="Address"><TextField size="small" fullWidth value={address} onChange={(e) => setAddress(e.target.value)} sx={fsx} multiline minRows={2} /></FR>
              <FR l="Enterprise Site"><TextField size="small" fullWidth value={enterpriseSite} onChange={(e) => setEnterpriseSite(e.target.value)} sx={fsx} /></FR>
              <FR l="Contact"><TextField size="small" fullWidth value={contactField} onChange={(e) => setContactField(e.target.value)} sx={fsx} /></FR>
              <FR l="Phone"><TextField size="small" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} sx={fsx} /></FR>
              <FR l="Fax"><TextField size="small" fullWidth value={fax} onChange={(e) => setFax(e.target.value)} sx={fsx} /></FR>
              <FR l="Timezone"><TextField size="small" fullWidth select value={timeZone} onChange={(e) => setTimeZone(e.target.value)} sx={fsx}><MenuItem value="">—</MenuItem>{TIMEZONES.map((tz) => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}</TextField></FR>
              <FR l="Date Format"><TextField size="small" fullWidth select value={dateFormat} onChange={(e) => setDateFormat(Number(e.target.value))} sx={fsx}>{DATE_FORMATS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}</TextField></FR>
              <FR l="Date Separator"><TextField size="small" fullWidth select value={dateSeparator} onChange={(e) => setDateSeparator(Number(e.target.value))} sx={fsx}>{DATE_SEPARATORS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}</TextField></FR>
              <FR l="Clock Format"><TextField size="small" fullWidth select value={clockFormat} onChange={(e) => setClockFormat(Number(e.target.value))} sx={fsx}>{CLOCK_FORMATS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}</TextField></FR>
              <FR l="Language"><TextField size="small" fullWidth select value={language} onChange={(e) => setLanguage(e.target.value)} sx={fsx}>{LANGUAGES.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}</TextField></FR>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={secSx}>Number Generation</Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                {([['Accession Number', accPfx, setAccPfx, accLen, setAccLen, accSt, setAccSt],
                   ['Patient ID', pidPfx, setPidPfx, pidLen, setPidLen, pidSt, setPidSt],
                   ['Insurance Billing ID', bidPfx, setBidPfx, bidLen, setBidLen, bidSt, setBidSt]] as const).map(([title, pfx, setPfx, len, setLen, st, setSt]) => (
                  <Box key={title} sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.5, textAlign: 'center' }}>{title}</Typography>
                    <FR l="Prefix"><TextField size="small" fullWidth value={pfx} onChange={(e) => (setPfx as (v: string) => void)(e.target.value)} sx={fsx} /></FR>
                    <FR l="Min Length"><TextField size="small" fullWidth type="number" value={len} onChange={(e) => (setLen as (v: number) => void)(Number(e.target.value))} sx={fsx} /></FR>
                    <FR l="Start"><TextField size="small" fullWidth type="number" value={st} onChange={(e) => (setSt as (v: number) => void)(Number(e.target.value))} sx={fsx} /></FR>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={secSx}>Registration &amp; Access</Typography>
              <CR l="Enable Open Patient Registration" c={openReg} o={setOpenReg} />
              <CR l="Add Patient to Enterprise Site" c={addToEnterprise} o={setAddToEnterprise} />
              <CR l="Allow Un-authenticated upload of anonymized studies" c={anonUpload} o={setAnonUpload} />
              <CR l="Allow site administrator to set retention" c={!noRetention} o={(v) => setNoRetention(!v)} />
              <CR l="Redirect to Site URL" c={redirect} o={setRedirect} />
            </Box>}
            {siteInfoTab === 1 && <Box>
              <Typography variant="body2" sx={{ fontSize: 11, color: 'text.secondary', mb: 1.5 }}>
                Configure business hours for each day. Affects scheduling, urgent escalation, and SLA.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '120px 80px 90px 90px', gap: 0.5, alignItems: 'center' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Day</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Status</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Open</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Close</Typography>
                {DAYS.map((day) => {
                  const h = hours[day] ?? { doors: 'Closed' };
                  return [
                    <Typography key={`${day}-n`} sx={{ fontSize: 12 }}>{day.charAt(0) + day.slice(1).toLowerCase()}</Typography>,
                    <TextField key={`${day}-d`} size="small" select value={h.doors} onChange={(e) => setH(day, 'doors', e.target.value)} sx={{ ...fsx, '& .MuiInputBase-root': { fontSize: 11 } }}>
                      {['Open', 'Closed'].map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 11 }}>{s}</MenuItem>)}
                    </TextField>,
                    <TextField key={`${day}-o`} size="small" value={h.openTime ?? ''} onChange={(e) => setH(day, 'openTime', e.target.value)} disabled={h.doors === 'Closed'} placeholder="HH:MM" sx={{ ...fsx, '& .MuiInputBase-root': { fontSize: 11 } }} />,
                    <TextField key={`${day}-c`} size="small" value={h.closeTime ?? ''} onChange={(e) => setH(day, 'closeTime', e.target.value)} disabled={h.doors === 'Closed'} placeholder="HH:MM" sx={{ ...fsx, '& .MuiInputBase-root': { fontSize: 11 } }} />,
                  ];
                })}
              </Box>
            </Box>}
          </>}

          {/* ── DICOM ── */}
          {cat === 'dicom' && <Box>
            <Typography variant="subtitle2" sx={secSx}>DICOM Settings</Typography>
            <FR l="Primary AE Title *">
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <TextField size="small" fullWidth value={aeTitle} onChange={(e) => setAeTitle(e.target.value)} sx={fsx} />
                <DicomEchoButton aetitle={aeTitle} siteObjKey={site.objKey} />
              </Box>
            </FR>
            <FR l="Auto-complete">
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <TextField size="small" type="number" value={acTime} onChange={(e) => setAcTime(Number(e.target.value))} sx={{ ...fsx, width: 60 }} />
                <TextField size="small" select value={acScale} onChange={(e) => setAcScale(e.target.value)} sx={{ ...fsx, width: 80 }}>
                  <MenuItem value="m">min</MenuItem><MenuItem value="h">hours</MenuItem><MenuItem value="d">days</MenuItem>
                </TextField>
              </Box>
            </FR>
            <FR l="Secondary AE"><TextField size="small" fullWidth value={secAeTitle} onChange={(e) => setSecAeTitle(e.target.value)} sx={fsx} /></FR>
            <FR l="Auto-complete">
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <TextField size="small" type="number" value={sacTime} onChange={(e) => setSacTime(Number(e.target.value))} sx={{ ...fsx, width: 60 }} />
                <TextField size="small" select value={sacScale} onChange={(e) => setSacScale(e.target.value)} sx={{ ...fsx, width: 80 }}>
                  <MenuItem value="m">min</MenuItem><MenuItem value="h">hours</MenuItem><MenuItem value="d">days</MenuItem>
                </TextField>
              </Box>
            </FR>
            <FR l="Comparison AE"><TextField size="small" fullWidth value={compAeTitle} onChange={(e) => setCompAeTitle(e.target.value)} sx={fsx} /></FR>
            <CR l="Allow Non-Strict DICOM" c={allowNonStrictDicom} o={setAllowNonStrictDicom} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={secSx}>Storage Retention</Typography>
            <FR l="Replication"><TextField size="small" type="number" value={replication} onChange={(e) => setReplication(Number(e.target.value))} sx={{ ...fsx, width: 80 }} /></FR>
            <FR l="Retain Viewed">
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <TextField size="small" type="number" value={retainViewed} onChange={(e) => setRetainViewed(Number(e.target.value))} sx={{ ...fsx, width: 60 }} />
                <TextField size="small" select value={retainUnit} onChange={(e) => setRetainUnit(e.target.value)} sx={{ ...fsx, width: 100 }}>
                  {['DAYS', 'WEEKS', 'MONTHS', 'YEARS'].map((u) => <MenuItem key={u} value={u}>{u.charAt(0) + u.slice(1).toLowerCase()}</MenuItem>)}
                </TextField>
              </Box>
            </FR>
            <FR l="Purge Type"><TextField size="small" select fullWidth value={purgeType} onChange={(e) => setPurgeType(e.target.value)} sx={fsx}>
              <MenuItem value="NEVER">Never</MenuItem><MenuItem value="DELETEIMAGES">Delete Images Only</MenuItem><MenuItem value="DELETESTUDY">Delete Entire Study</MenuItem>
            </TextField></FR>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={secSx}>AI Validation</Typography>
            <ROJson data={((d.features as Record<string, unknown>)?.aiValidation ?? {}) as Record<string, unknown>} empty="No AI validation rules configured." />
          </Box>}

          {/* ── HL7 ── */}
          {cat === 'hl7' && <HL7ConfigEditor site={site} d={d} onSaved={onSave} />}

          {/* ── Login Configuration ── */}
          {cat === 'loginConfig' && <LoginConfigEditor site={site} d={d} onSaved={onSave} />}

          {/* ── Feature-gated categories — use FeatureConfigEditor with correct keys ── */}
          {cat === 'urgentWorks' && <LFGate lf={lf} f="UrgentFindings" l="Urgent Findings"><FeatureConfigEditor site={site} d={d} onSaved={onSave} featureKey="urgentFindings" /></LFGate>}
          {cat === 'discordance' && <LFGate lf={lf} f="Discordance" l="Discordance"><FeatureConfigEditor site={site} d={d} onSaved={onSave} featureKey="discordance" /></LFGate>}
          {cat === 'peerReview' && <LFGate lf={lf} f="PeerReview" l="Peer Review"><FeatureConfigEditor site={site} d={d} onSaved={onSave} featureKey="peerReview" /></LFGate>}
          {cat === 'patientDiscovery' && <LFGate lf={lf} f="PatientDiscovery" l="Patient Discovery"><FeatureConfigEditor site={site} d={d} onSaved={onSave} featureKey="patientDiscovery" /></LFGate>}
          {cat === 'dictation' && <LFGate lf={lf} f="SpeechAnywhere" l="Speech Anywhere"><FeatureConfigEditor site={site} d={d} onSaved={onSave} featureKey="speechAnywhere" /></LFGate>}
          {cat === 'qc' && <LFGate lf={lf} f="QC" l="QC"><FeatureConfigEditor site={site} d={d} onSaved={onSave} featureKey="qc" /></LFGate>}
          {cat === 'notifications' && <LFGate lf={lf} f="SendingTextMessages" l="Text Messages"><FeatureConfigEditor site={site} d={d} onSaved={onSave} featureKey="textMessages" /></LFGate>}
          {cat === 'viewerInterfaces' && <LFGate lf={lf} f="CustomActions" l="Custom Actions"><FeatureConfigEditor site={site} d={d} onSaved={onSave} featureKey="customActions" /></LFGate>}

          {/* ── Real form editors for config categories ── */}
          {cat === 'security' && <SecurityEditor site={site} d={d} onSaved={onSave} />}
          {cat === 'insurancePayment' && <InsurancePaymentEditor site={site} d={d} onSaved={onSave} />}
          {cat === 'insuranceCompanies' && <InsuranceCompaniesEditor siteObjKey={site.objKey} />}
          {cat === 'imageViewing' && <ImageViewingEditor site={site} d={d} onSaved={onSave} />}
          {cat === 'reportConfig' && <ReportConfigEditor site={site} d={d} onSaved={onSave} />}
          {cat === 'siteDefaults' && <SiteDefaultsEditor site={site} d={d} onSaved={onSave} />}
          {cat === 'readingWorkflow' && <LFGate lf={lf} f="AdvancedReadingWorkflow" l="Advanced Reading Workflow"><ReadingWorkflowEditor site={site} d={d} onSaved={onSave} /></LFGate>}
          {cat === 'cdsWorkflow' && <LFGate lf={lf} f="CdsRadwise" l="CDS RadWise"><CDSWorkflowEditor site={site} d={d} onSaved={onSave} /></LFGate>}

          {/* ── Categories with fetched ObjectItem data ── */}
          {cat === 'procedures' && <ProceduresEditor siteObjKey={site.objKey} />}
          {cat === 'panelConfig' && <PanelConfigEditor site={site} d={d} onSaved={onSave} />}
          {cat === 'accessGroups' && <AccessGroupsEditor siteObjKey={site.objKey} />}
          {cat === 'userRoles' && <UserRolesEditor siteObjKey={site.objKey} />}
          {cat === 'macros' && <SiteMacrosEditor siteObjKey={site.objKey} />}
          {cat === 'logs' && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Server logs viewable in the Log Viewer admin tab.</Typography>}

          {/* ── Licensed Features ── */}
          {cat === 'licensedFeatures' && <Box>
            <Typography variant="body2" sx={{ fontSize: 11, color: 'text.secondary', mb: 1.5 }}>Licensed features for this site. Contact BRIT Systems to modify.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.25 }}>
              {Object.entries(lf).filter(([, v]) => typeof v === 'boolean').map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip label={v ? 'ON' : 'OFF'} size="small" color={v ? 'success' : 'default'} sx={{ fontSize: 9, height: 16, width: 32 }} />
                  <Typography sx={{ fontSize: 11 }}>{k}</Typography>
                </Box>
              ))}
            </Box>
          </Box>}

          {/* ── Billing Invoice ── */}
          {cat === 'billingInvoice' && <BillingEditor site={site} d={d} onSaved={onSave} />}
        </Box>
      </Box>

      <DialogActions sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Button size="small" onClick={onClose} sx={btnSx}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving || !name.trim() || (isNew === true && !siteId.trim())} sx={btnSx}>{saving ? (isNew ? 'Creating...' : 'Saving...') : (isNew ? 'Create Site' : 'Save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Site category sub-panels that fetch via JSON-RPC ── */

const DICOM_MODALITIES = ['CT','MR','CR','DX','US','NM','PT','MG','XA','RF','OT','SC','IO','PR','SR','RTDOSE','RTSTRUCT','RTPLAN'] as const;
interface ProcFormState { procCode: string; codeMeaning: string; modality: string; bodyPart: string; minTime: number; mrvu: number; prepNotes: string; }
const EMPTY_PROC_FORM: ProcFormState = { procCode: '', codeMeaning: '', modality: 'CT', bodyPart: '', minTime: 0, mrvu: 0, prepNotes: '' };

/** Procedures editor — full CRUD for a site's procedure definitions */
const ProceduresEditor = ({ siteObjKey }: { siteObjKey: string }) => {
  const qc = useQueryClient();
  const { data: procsData, isLoading } = useQuery({
    queryKey: ['procDefs', siteObjKey],
    queryFn: () => api.getProcedureDefinitionsForSite(siteObjKey),
    staleTime: 30_000,
  });
  const { data: bodyPartsData } = useQuery({
    queryKey: ['bodyParts'],
    queryFn: () => api.getBodyParts(),
    staleTime: 5 * 60_000,
  });

  type ProcRec = NonNullable<typeof procsData>['records'][number];

  const allProcs = useMemo(() => (procsData?.records ?? []) as ProcRec[], [procsData]);
  const bodyParts = useMemo(() => Array.isArray(bodyPartsData) ? (bodyPartsData as string[]) : [], [bodyPartsData]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProc, setEditingProc] = useState<ProcRec | null>(null);
  const [form, setForm] = useState<ProcFormState>(EMPTY_PROC_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProcRec | null>(null);

  const invalidate = useCallback(() => qc.invalidateQueries({ queryKey: ['procDefs', siteObjKey] }), [qc, siteObjKey]);

  const openAdd = () => { setEditingProc(null); setForm(EMPTY_PROC_FORM); setDialogOpen(true); };
  const openEdit = (p: ProcRec) => {
    setEditingProc(p);
    setForm({ procCode: p.procCode, codeMeaning: p.codeMeaning, modality: p.modality, bodyPart: p.bodyPart, minTime: p.minTime, mrvu: p.mrvu ?? 0, prepNotes: p.prepNotes ?? '' });
    setDialogOpen(true);
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const mKey = editingProc?.modalityKey ?? allProcs.find(p => p.modality === form.modality)?.modalityKey ?? form.modality;
      let updated: ProcRec[];
      if (editingProc) {
        updated = allProcs.filter(p => (p.modalityKey ?? p.modality) === mKey)
          .map(p => p.objKey === editingProc.objKey ? { ...p, ...form, minTimeInMinutes: form.minTime } : p);
      } else {
        const existing = allProcs.filter(p => (p.modalityKey ?? p.modality) === mKey);
        updated = [...existing, { ...form, modalityKey: mKey, objKey: '', customized: true, minTimeInMinutes: form.minTime, laterality: '', codeDesig: '' } as ProcRec];
      }
      await api.updateProcedureDefinitionsForModality(mKey, updated);
      pushSuccess(`Procedure ${editingProc ? 'updated' : 'added'}`);
      setDialogOpen(false);
      invalidate();
    } catch (e) { pushError(`Failed: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [allProcs, editingProc, form, invalidate]);

  const handleDelete = useCallback(async (p: ProcRec) => {
    const mKey = p.modalityKey ?? p.modality;
    const remaining = allProcs.filter(x => (x.modalityKey ?? x.modality) === mKey && x.objKey !== p.objKey);
    try {
      await api.updateProcedureDefinitionsForModality(mKey, remaining);
      pushSuccess('Procedure deleted');
      invalidate();
    } catch (e) { pushError(`Delete failed: ${e instanceof Error ? e.message : String(e)}`); }
    setDeleteTarget(null);
  }, [allProcs, invalidate]);

  if (isLoading) return <CircularProgress size={16} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 600 }}>Procedures ({allProcs.length})</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openAdd} sx={{ textTransform: 'none', fontSize: 11 }}>Add</Button>
      </Box>
      {allProcs.length === 0
        ? <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>No procedures defined. Use Add to create one.</Typography>
        : <Table size="small" sx={{ '& td,& th': { fontSize: 11, py: 0.25 } }}>
            <TableHead><TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Meaning</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Modality</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Body Part</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Min Time</TableCell>
              <TableCell />
            </TableRow></TableHead>
            <TableBody>
              {allProcs.map((p) => (
                <TableRow key={p.objKey || p.procCode}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{p.procCode}</TableCell>
                  <TableCell>{p.codeMeaning}</TableCell>
                  <TableCell>{p.modality}</TableCell>
                  <TableCell>{p.bodyPart}</TableCell>
                  <TableCell>{p.minTime >= 0 ? `${p.minTime}m` : '—'}</TableCell>
                  <TableCell sx={{ py: 0, whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => openEdit(p)} sx={{ p: 0.25 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                    <IconButton size="small" onClick={() => setDeleteTarget(p)} sx={{ p: 0.25, color: 'error.main' }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
      }

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 13, fontWeight: 600, pb: 0 }}>{editingProc ? 'Edit Procedure' : 'Add Procedure'}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
            <TextField label="Procedure Code *" size="small" value={form.procCode}
              onChange={(e) => setForm(f => ({ ...f, procCode: e.target.value.toUpperCase() }))}
              inputProps={{ maxLength: 16 }} sx={{ '& .MuiInputBase-root': { fontSize: 12 } }} />
            <TextField label="Modality *" size="small" select value={form.modality}
              onChange={(e) => setForm(f => ({ ...f, modality: e.target.value }))} sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}>
              {DICOM_MODALITIES.map((m) => <MenuItem key={m} value={m} sx={{ fontSize: 12 }}>{m}</MenuItem>)}
            </TextField>
            <TextField label="Code Meaning / Description *" size="small" value={form.codeMeaning}
              onChange={(e) => setForm(f => ({ ...f, codeMeaning: e.target.value }))}
              sx={{ gridColumn: '1/-1', '& .MuiInputBase-root': { fontSize: 12 } }} />
            <Autocomplete size="small" options={bodyParts} value={form.bodyPart || null} freeSolo
              onChange={(_e, v) => setForm(f => ({ ...f, bodyPart: v ?? '' }))}
              renderInput={(params) => <TextField {...params} label="Body Part" sx={{ '& .MuiInputBase-root': { fontSize: 12 } }} />} />
            <TextField label="Min Time (min)" size="small" type="number" value={form.minTime}
              onChange={(e) => setForm(f => ({ ...f, minTime: Number(e.target.value) }))}
              sx={{ '& .MuiInputBase-root': { fontSize: 12 } }} />
            <TextField label="RVU" size="small" type="number" value={form.mrvu}
              onChange={(e) => setForm(f => ({ ...f, mrvu: Number(e.target.value) }))}
              sx={{ '& .MuiInputBase-root': { fontSize: 12 } }} />
            <TextField label="Prep Notes" size="small" multiline rows={2} value={form.prepNotes}
              onChange={(e) => setForm(f => ({ ...f, prepNotes: e.target.value }))}
              sx={{ gridColumn: '1/-1', '& .MuiInputBase-root': { fontSize: 12 } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button size="small" onClick={() => setDialogOpen(false)} disabled={saving} sx={btnSx}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSave}
            disabled={saving || !form.procCode.trim() || !form.codeMeaning.trim()} sx={btnSx}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Procedure"
        message={`Remove "${deleteTarget?.procCode} — ${deleteTarget?.codeMeaning}"? This cannot be undone.`}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

/** Access Groups list for a site */
const ACCESS_GROUP_TYPES = ['LOCATION', 'MODALITY', 'PHYSICIAN'] as const;
type AccessGroup = { objKey: string; name: string; id: string; type: string };

const AccessGroupsEditor = ({ siteObjKey }: { siteObjKey: string }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['accessGroups', siteObjKey],
    queryFn: () => api.getAccessGroupsBySite(siteObjKey),
    staleTime: 30_000,
  });
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState<AccessGroup | null>(null);
  const [form, setForm] = useState({ id: '', name: '', type: 'LOCATION' as string });
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState<AccessGroup | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['accessGroups', siteObjKey] });

  const openAdd = () => { setEditing(null); setForm({ id: '', name: '', type: 'LOCATION' }); setDlgOpen(true); };
  const openEdit = (g: AccessGroup) => { setEditing(g); setForm({ id: g.id, name: g.name, type: g.type }); setDlgOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      if (editing) {
        await api.updateAccessGroup(siteObjKey, editing.objKey, form.name.trim());
        pushSuccess('Access group updated.');
      } else {
        if (!form.id.trim()) { pushError('Group ID is required.'); return; }
        await api.addAccessGroup(siteObjKey, form.id.trim(), form.name.trim(), form.type);
        pushSuccess('Access group created.');
      }
      setDlgOpen(false);
      await invalidate();
    } catch (e: unknown) {
      pushError(e instanceof Error ? e.message : 'Save failed.');
    } finally { setBusy(false); }
  };

  const handleDelete = async (g: AccessGroup) => {
    setBusy(true);
    try {
      await api.deleteAccessGroup(siteObjKey, g.objKey);
      pushSuccess(`Access group "${g.name}" deleted.`);
      await invalidate();
    } catch (e: unknown) {
      pushError(e instanceof Error ? e.message : 'Delete failed.');
    } finally { setBusy(false); setConfirmDel(null); }
  };

  if (isLoading) return <CircularProgress size={16} />;
  const groups = data?.records ?? [];
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 600, flexGrow: 1 }}>Access Groups ({groups.length})</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAdd}>Add</Button>
      </Box>
      <Table size="small" sx={{ '& td,& th': { fontSize: 11, py: 0.25 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.length === 0 && (
            <TableRow><TableCell colSpan={4}><Typography sx={{ fontSize: 11, color: 'text.secondary', fontStyle: 'italic' }}>No access groups defined.</Typography></TableCell></TableRow>
          )}
          {groups.map((g) => (
            <TableRow key={g.objKey}>
              <TableCell>{g.name}</TableCell>
              <TableCell>{g.type}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 10 }}>{g.id}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(g)}><EditIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirmDel(g)}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 14, pb: 1 }}>{editing ? 'Edit Access Group' : 'Add Access Group'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '12px !important' }}>
          {!editing && (
            <TextField label="Group ID" size="small" fullWidth value={form.id}
              onChange={(e) => setForm(f => ({ ...f, id: e.target.value }))}
              helperText="Short identifier (e.g. radiology-a)" />
          )}
          <TextField label="Name" size="small" fullWidth value={form.name}
            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
          {!editing && (
            <TextField label="Type" size="small" select fullWidth value={form.type}
              onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}>
              {ACCESS_GROUP_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDlgOpen(false)}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSave} disabled={busy || !form.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDel !== null}
        title="Delete Access Group"
        message={`Delete access group "${confirmDel?.name}"? This will also remove it from all member users.`}
        onConfirm={() => confirmDel && handleDelete(confirmDel)}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  );
};

/** Permission groups (mirrors GWT Permissions enum grouping, used for role editing) */
const ROLE_PERM_GROUPS: { group: string; perms: string[] }[] = [
  { group: 'Studies', perms: [
    'DOWNLOAD_STUDIES', 'SEND_STUDIES_TO', 'UPLOAD_STUDIES', 'DOWNLOAD_FOR_CD', 'ADD_STUDY_SHARES',
    'COMPLETE_STUDIES', 'ASSIGN_STUDIES', 'RELEASE_STUDIES', 'ASSIGN_STUDIES_TO_RESIDENT',
    'HOLD_UNHOLD_STUDIES', 'MARK_READ', 'MARK_UNREAD', 'MARK_DICTATED', 'MARK_TRANSCRIBED',
    'MARK_INPROGRESS', 'EDIT_STUDIES', 'EDIT_NON_ASSIGNED_STUDIES',
    'DELETE_PURE_STUDIES', 'DELETE_ANY_STUDIES', 'DELETE_IMAGES_PURE_STUDIES', 'DELETE_IMAGES_ANY_STUDIES',
    'VIEW_STUDY_EVENTS', 'ASSIGN_TO_ME', 'EDIT_MISSED_SLA_REASONS', 'CALL_CONTACTS', 'AUTO_ADD_ORDERS',
    'VIEW_DICOM_DUMP', 'UPLOAD_MATCHING',
  ] },
  { group: 'Reports', perms: [
    'VIEW_DRAFT_REPORTS', 'ADD_DRAFT_REPORTS', 'SIGN_REPORTS', 'ADD_ADDENDUMS', 'SIGN_ADDENDUMS',
    'SEND_REPORTS_VIA_HL7', 'VIEW_FAX_QUEUE', 'TRANSCRIBE_REPORTS',
  ] },
  { group: 'Dictations', perms: ['DICTATE', 'DELETE_DICTATIONS'] },
  { group: 'Annotations', perms: ['CREATE_ANNOTATIONS', 'CLEAR_ANNOTATIONS'] },
  { group: 'Discordance', perms: ['VIEW_DISCORDANCE', 'ACKNOWLEDGE_DISCORDANCE', 'ADD_EDIT_ER_CALL', 'ADD_EDIT_RAD_CALL'] },
  { group: 'Peer Review', perms: ['PERFORM_REER_REVIEW', 'REQUEST_PEER_REVIEW'] },
  { group: 'Attachments', perms: ['ATTACH_FILES', 'DELETE_ATTACHMENTS', 'EDIT_LABEL_COMMENTS_ATTACHMENTS', 'ASSIGN_UNASSIGN_ATTACHMENTS'] },
  { group: 'Orders', perms: ['ADD_ORDERS', 'EDIT_ORDERS', 'DELETE_ORDERS', 'SEND_ORDERS_VIA_HL7', 'INSURANCE_AUTHORIZATION', 'ARRIVE_PATIENT', 'MATCH_WITH_ORDER'] },
  { group: 'Patients', perms: ['ADD_PATIENTS', 'EDIT_PATIENTS', 'DELETE_PATIENTS', 'PATIENT_DISCOVERY', 'EDIT_PATIENT_ID'] },
  { group: 'Notes', perms: [
    'VIEW_STUDY_NOTES', 'EDIT_STUDY_NOTES', 'DELETE_STUDY_NOTES',
    'VIEW_REPORT_NOTES', 'EDIT_REPORT_NOTES', 'DELETE_REPORT_NOTES',
    'VIEW_ORDER_NOTES', 'EDIT_ORDER_NOTES', 'DELETE_ORDER_NOTES',
    'VIEW_PRIVATE_NOTES',
  ] },
  { group: 'QC', perms: ['QC_OPEN_EDITOR', 'QC_MARK_UNMARK', 'QC_BREAK_SESSION'] },
  { group: 'Users/Roles', perms: [
    'ADD_EDIT_ADMINISTRATORS', 'DELETE_ADMINISTRATORS',
    'ADD_EDIT_REPORTING_PHYSICIANS', 'DELETE_REPORTING_PHYSICIANS',
    'ADD_EDIT_RESIDENTS', 'DELETE_RESIDENTS',
    'ADD_EDIT_MANAGERS', 'DELETE_MANAGERS',
    'ADD_EDIT_TECHS', 'DELETE_TECHS',
    'ADD_EDIT_TRANSCRIPTIONISTS', 'DELETE_TRANSCRIPTIONISTS',
    'ADD_EDIT_CLERKS', 'DELETE_CLERKS',
    'ADD_EDIT_NURSES', 'DELETE_NURSES',
    'ADD_EDIT_PHYSICIANS', 'DELETE_PHYSICIANS',
    'MOVE_USERS', 'MERGE_USERS',
  ] },
  { group: 'Sites', perms: ['ADD_EDIT_CUSTOM_ROLES', 'DELETE_CUSTOM_ROLES', 'EDIT_SYSTEM_ROLES'] },
  { group: 'Locations/Modalities', perms: ['ADD_EDIT_LOCATIONS', 'ADD_EDIT_MODALITIES', 'DELETE_LOCATIONS', 'DELETE_MODALITIES'] },
  { group: 'Insurance', perms: ['ADD_INSURANCE_COMPANY', 'EDIT_INSURANCE_COMPANY', 'DELETE_INSURANCE_COMPANY'] },
  { group: 'Quizzes', perms: ['CREATE_QUIZZES', 'TAKE_QUIZZES'] },
];

type RolePermEntry = { roleObjKey: string; roleName: string; description: string; permissions: Array<{ permName: string; allow: boolean }> };

/** User Roles editor — shows all roles, click Edit to toggle permissions */
const UserRolesEditor = ({ siteObjKey }: { siteObjKey: string }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['siteRolePerms', siteObjKey],
    queryFn: () => api.getSiteRolePermissions(siteObjKey),
    staleTime: 30_000,
  });

  const roles = useMemo(() => (data?.roles ?? []) as RolePermEntry[], [data]);

  const [editingRole, setEditingRole] = useState<RolePermEntry | null>(null);
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const openRole = (r: RolePermEntry) => {
    setEditingRole(r);
    setGranted(new Set(r.permissions.filter(p => p.allow).map(p => p.permName)));
  };

  const togglePerm = (permName: string, checked: boolean) => {
    setGranted(prev => {
      const next = new Set(prev);
      if (checked) next.add(permName); else next.delete(permName);
      return next;
    });
  };

  const handleSave = async () => {
    if (!editingRole) return;
    setSaving(true);
    try {
      const payload = [{
        roleObjKey: editingRole.roleObjKey,
        permissions: editingRole.permissions.map(p => ({ permName: p.permName, allow: granted.has(p.permName) })),
      }];
      await api.saveSiteRolePermissions(siteObjKey, payload);
      await qc.invalidateQueries({ queryKey: ['siteRolePerms', siteObjKey] });
      await qc.invalidateQueries({ queryKey: ['userRoles', siteObjKey] });
      setEditingRole(null);
      pushSuccess('Role permissions saved.');
    } catch {
      pushError('Failed to save role permissions.');
    } finally { setSaving(false); }
  };

  const labelOf = (perm: string) =>
    perm.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()).replace(/\bReer\b/, 'Peer');

  if (isLoading) return <CircularProgress size={16} />;
  if (roles.length === 0) return <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>No user roles found.</Typography>;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}>User Roles ({roles.length})</Typography>
      <Table size="small" sx={{ '& td,& th': { fontSize: 11, py: 0.25 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {roles.map((r) => {
            const grantedCount = r.permissions.filter(p => p.allow).length;
            return (
              <TableRow key={r.roleName}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 10 }}>{r.roleName}</TableCell>
                <TableCell>{r.description}</TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 11, color: grantedCount === 0 ? 'text.disabled' : 'text.primary' }}>
                    {grantedCount} / {r.permissions.length}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openRole(r)} sx={{ p: 0.25 }}>
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Permission editor dialog */}
      <Dialog open={!!editingRole} onClose={() => setEditingRole(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 13, py: 1 }}>
          Edit Permissions: {editingRole?.description ?? editingRole?.roleName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {ROLE_PERM_GROUPS.map(({ group, perms: groupPerms }) => {
              const available = groupPerms.filter(p => editingRole?.permissions.some(ep => ep.permName === p));
              if (available.length === 0) return null;
              return (
                <Box key={group} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: 11, fontWeight: 600, mb: 0.25, borderBottom: '1px solid', borderColor: 'divider', pb: 0.25 }}>
                    {group}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {available.map(permName => (
                      <FormControlLabel
                        key={permName}
                        control={
                          <Checkbox
                            size="small"
                            checked={granted.has(permName)}
                            onChange={(e) => togglePerm(permName, e.target.checked)}
                            sx={{ p: 0.25 }}
                          />
                        }
                        label={<Typography sx={{ fontSize: 11 }}>{labelOf(permName)}</Typography>}
                        sx={{ mr: 1, mb: 0.25 }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 0.5 }}>
          <Button size="small" onClick={() => setEditingRole(null)} sx={{ fontSize: 11 }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSave} disabled={saving} sx={{ fontSize: 11 }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ── Insurance Companies ── */
const InsuranceCompaniesEditor = ({ siteObjKey }: { siteObjKey: string }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['insuranceCompanies', siteObjKey],
    queryFn: () => api.getInsuranceCompanyList(siteObjKey),
    staleTime: 30_000,
  });

  const companies = useMemo(() => (data ?? []) as InsuranceCompanyBean[], [data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<InsuranceCompanyBean | null>(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '' });
  const [saving, setSaving] = useState(false);

  const openAdd = () => { setEditingCompany(null); setForm({ name: '', address: '', phone: '' }); setDialogOpen(true); };
  const openEdit = (c: InsuranceCompanyBean) => {
    setEditingCompany(c);
    setForm({ name: c.name ?? '', address: c.address ?? '', phone: c.phone ?? '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const bean: InsuranceCompanyBean = {
        ...(editingCompany ?? {}),
        name: form.name.trim(),
        address: form.address || undefined,
        phone: form.phone || undefined,
      };
      await api.addInsuranceCompany(siteObjKey, bean);
      await qc.invalidateQueries({ queryKey: ['insuranceCompanies', siteObjKey] });
      setDialogOpen(false);
    } finally { setSaving(false); }
  };

  if (isLoading) return <CircularProgress size={16} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 600, flex: 1 }}>Insurance Companies ({companies.length})</Typography>
        <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={openAdd} sx={{ fontSize: 11, py: 0.25 }}>Add</Button>
      </Box>
      {companies.length === 0
        ? <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>No insurance companies defined.</Typography>
        : (
          <Table size="small" sx={{ '& td,& th': { fontSize: 11, py: 0.25 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((c, i) => (
                <TableRow key={c.objKey ?? i}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.address ?? ''}</TableCell>
                  <TableCell>{c.phone ?? ''}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(c)} sx={{ p: 0.25 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 13, py: 1 }}>{editingCompany ? 'Edit Insurance Company' : 'Add Insurance Company'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} size="small" inputProps={{ style: { fontSize: 12 } }} InputLabelProps={{ style: { fontSize: 12 } }} required />
            <TextField label="Address" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} size="small" inputProps={{ style: { fontSize: 12 } }} InputLabelProps={{ style: { fontSize: 12 } }} />
            <TextField label="Phone" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} size="small" inputProps={{ style: { fontSize: 12 } }} InputLabelProps={{ style: { fontSize: 12 } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 0.5 }}>
          <Button size="small" onClick={() => setDialogOpen(false)} sx={{ fontSize: 11 }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSave} disabled={saving || !form.name.trim()} sx={{ fontSize: 11 }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ── Site Macros ── */
type MacroFormState = { name: string; text: string; cpt: string; mod: string; bod: string; descr: string; isSectionMacros: boolean };
const EMPTY_MACRO_FORM: MacroFormState = { name: '', text: '', cpt: '', mod: '', bod: '', descr: '', isSectionMacros: false };

const SiteMacrosEditor = ({ siteObjKey }: { siteObjKey: string }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['siteMacros', siteObjKey],
    queryFn: () => api.getSiteMacros(siteObjKey),
    staleTime: 30_000,
  });

  const macros = useMemo(() => (data?.records ?? []) as MacroBean[], [data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMacro, setEditingMacro] = useState<MacroBean | null>(null);
  const [form, setForm] = useState<MacroFormState>(EMPTY_MACRO_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MacroBean | null>(null);

  const openAdd = () => {
    setEditingMacro(null);
    setForm(EMPTY_MACRO_FORM);
    setDialogOpen(true);
  };
  const openEdit = (m: MacroBean) => {
    setEditingMacro(m);
    setForm({ name: m.name ?? '', text: m.text ?? '', cpt: m.cpt ?? '', mod: m.mod ?? '', bod: m.bod ?? '', descr: m.descr ?? '', isSectionMacros: !!m.isSectionMacros });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const bean: Partial<MacroBean> = {
        ...(editingMacro ?? {}),
        name: form.name.trim(),
        text: form.text,
        cpt: form.cpt || undefined,
        mod: form.mod || undefined,
        bod: form.bod || undefined,
        descr: form.descr || undefined,
        isSectionMacros: form.isSectionMacros || undefined,
        baseSite: siteObjKey,
      };
      await api.saveMacros({ userObjKey: siteObjKey, macros: [bean as MacroBean], isDel: false });
      await qc.invalidateQueries({ queryKey: ['siteMacros', siteObjKey] });
      setDialogOpen(false);
    } finally { setSaving(false); }
  };

  const handleDelete = async (m: MacroBean) => {
    setSaving(true);
    try {
      await api.saveMacros({ userObjKey: siteObjKey, macros: [m], isDel: true });
      await qc.invalidateQueries({ queryKey: ['siteMacros', siteObjKey] });
    } finally { setSaving(false); setDeleteTarget(null); }
  };

  if (isLoading) return <CircularProgress size={16} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 600, flex: 1 }}>Site Macros ({macros.length})</Typography>
        <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={openAdd} sx={{ fontSize: 11, py: 0.25 }}>Add</Button>
      </Box>

      {macros.length === 0
        ? <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>No macros defined.</Typography>
        : (
          <Table size="small" sx={{ '& td,& th': { fontSize: 11, py: 0.25 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Modality</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>CPT</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Text preview</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {macros.map((m, i) => (
                <TableRow key={m.objKey ?? i}>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.isSectionMacros ? 'Section' : 'Standard'}</TableCell>
                  <TableCell>{m.mod ?? ''}</TableCell>
                  <TableCell>{m.cpt ?? ''}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.text ?? ''}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => openEdit(m)} sx={{ p: 0.25 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                    <IconButton size="small" onClick={() => setDeleteTarget(m)} sx={{ p: 0.25, ml: 0.5 }} color="error"><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 13, py: 1 }}>{editingMacro ? 'Edit Macro' : 'Add Macro'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, pt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} size="small" inputProps={{ style: { fontSize: 12 } }} InputLabelProps={{ style: { fontSize: 12 } }} required />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Checkbox size="small" checked={form.isSectionMacros} onChange={(e) => setForm(f => ({ ...f, isSectionMacros: e.target.checked }))} sx={{ p: 0.25 }} />
              <Typography sx={{ fontSize: 12 }}>Section macro</Typography>
            </Box>
            <TextField label="Modality" value={form.mod} onChange={(e) => setForm(f => ({ ...f, mod: e.target.value }))} size="small" inputProps={{ style: { fontSize: 12 } }} InputLabelProps={{ style: { fontSize: 12 } }} />
            <TextField label="CPT Code" value={form.cpt} onChange={(e) => setForm(f => ({ ...f, cpt: e.target.value }))} size="small" inputProps={{ style: { fontSize: 12 } }} InputLabelProps={{ style: { fontSize: 12 } }} />
            <TextField label="Body Part" value={form.bod} onChange={(e) => setForm(f => ({ ...f, bod: e.target.value }))} size="small" inputProps={{ style: { fontSize: 12 } }} InputLabelProps={{ style: { fontSize: 12 } }} />
            <TextField label="Description" value={form.descr} onChange={(e) => setForm(f => ({ ...f, descr: e.target.value }))} size="small" inputProps={{ style: { fontSize: 12 } }} InputLabelProps={{ style: { fontSize: 12 } }} />
            <TextField label="Macro Text" value={form.text} onChange={(e) => setForm(f => ({ ...f, text: e.target.value }))} size="small" multiline minRows={4} inputProps={{ style: { fontSize: 12 } }} InputLabelProps={{ style: { fontSize: 12 } }} sx={{ gridColumn: '1 / -1' }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 0.5 }}>
          <Button size="small" onClick={() => setDialogOpen(false)} sx={{ fontSize: 11 }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSave} disabled={saving || !form.name.trim()} sx={{ fontSize: 11 }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Macro"
        message={`Delete macro "${deleteTarget?.name ?? ''}"? This cannot be undone.`}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

/* ── Shared sub-components ── */
const FR = ({ l, children }: { l: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 110, textAlign: 'right', flexShrink: 0, pt: 0.5 }}>{l}:</Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const CR = ({ l, c, o }: { l: string; c: boolean; o: (v: boolean) => void }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, ml: '118px' }}>
    <Checkbox size="small" checked={c} onChange={(e) => o(e.target.checked)} sx={{ p: 0.25, mr: 0.5 }} />
    <Typography variant="body2" sx={{ fontSize: 12 }}>{l}</Typography>
  </Box>
);

const LFGate = ({ lf, f, l, children }: { lf: Record<string, unknown>; f: string; l: string; children: React.ReactNode }) => {
  const ok = !!lf[f];
  return <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Chip label={ok ? 'Licensed' : 'Not Licensed'} size="small" color={ok ? 'success' : 'default'} sx={{ fontSize: 10, height: 18 }} />
      <Typography sx={{ fontSize: 12, color: ok ? 'text.primary' : 'text.disabled' }}>{l}</Typography>
    </Box>
    {ok ? children : <Typography sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>Not licensed for this site.</Typography>}
  </Box>;
};

const ROJson = ({ data, empty }: { data: unknown; empty: string }) => {
  const none = !data || (typeof data === 'object' && Object.keys(data as Record<string, unknown>).length === 0) || (Array.isArray(data) && data.length === 0);
  if (none) return <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>{empty}</Typography>;
  return <TextField size="small" fullWidth multiline minRows={6} maxRows={20} value={JSON.stringify(data, null, 2)}
    slotProps={{ input: { readOnly: true } }} sx={{ ...fsx, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 10 } }} />;
};

/** DICOM Echo test button — calls dicomEcho endpoint with the AE title */
const DicomEchoButton = ({ aetitle, siteObjKey }: { aetitle: string; siteObjKey: string }) => {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<'success' | 'fail' | null>(null);

  const handleEcho = async () => {
    if (!aetitle) return;
    setTesting(true);
    setResult(null);
    try {
      const ok = await api.dicomEcho(aetitle, 'localhost', 11112, siteObjKey);
      setResult(ok ? 'success' : 'fail');
      if (ok) pushSuccess(`DICOM Echo to "${aetitle}" succeeded`);
      else pushError(`DICOM Echo to "${aetitle}" failed — no response`);
    } catch (e) {
      setResult('fail');
      pushError(`DICOM Echo failed: ${e instanceof Error ? e.message : String(e)}`);
    }
    setTesting(false);
  };

  return (
    <Button size="small" variant="outlined"
      disabled={!aetitle || testing}
      onClick={handleEcho}
      color={result === 'success' ? 'success' : result === 'fail' ? 'error' : 'primary'}
      sx={{ fontSize: 10, textTransform: 'none', py: 0, minHeight: 28, minWidth: 70, whiteSpace: 'nowrap' }}>
      {testing ? 'Testing...' : result === 'success' ? '✓ OK' : result === 'fail' ? '✗ Fail' : 'Echo'}
    </Button>
  );
};

const fsx = { '& .MuiInputBase-root': { fontSize: 12 } };
const btnSx = { textTransform: 'none' as const, fontSize: 12 };
const tabSx = { textTransform: 'none' as const, fontSize: 12, minHeight: 32, py: 0 };
const secSx = { fontSize: 12, fontWeight: 600, mb: 1 };
